// apps/accounts/src/pages/SiteEmployees.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Building2, UserPlus, Search, RefreshCw, Trash2,
  CheckCircle, XCircle, Edit, Eye, Shield, Link2,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../lib/pocketbaseClient';

// --- Available Employee Roles ------------------------------------
const ROLES = [
  { value: 'site_engineer', label: 'Site Engineer' },
  { value: 'site_supervisor', label: 'Site Supervisor' },
  { value: 'store_keeper', label: 'Store Keeper' },
  { value: 'site_manager', label: 'Site Manager' },
  { value: 'labour_supervisor', label: 'Labour Supervisor' },
];

// --- Available Tabs for Employee Assignment --------------------
const AVAILABLE_TABS = [
  { path: '/projects', label: 'Projects' },
  { path: '/partners', label: 'Partners' },
  { path: '/vendors', label: 'Vendors' },
  { path: '/labour', label: 'Labour' },
  { path: '/attendance', label: 'Attendance' },
  { path: '/expenses', label: 'Project Expenses' },
  { path: '/vendor-payments', label: 'Vendor Payments' },
  { path: '/client-payments', label: 'Client Payments' },
  { path: '/investments', label: 'Fund / Investments' },
  { path: '/reports', label: 'Reports' },
  { path: '/materials', label: 'Materials List' },
  { path: '/materials/categories', label: 'Categories' },
  { path: '/materials/uoms', label: 'UOMs' },
  { path: '/materials/suppliers', label: 'Suppliers' },
  { path: '/materials/requests', label: 'Requests' },
  { path: '/materials/purchase-orders', label: 'Purchase Orders' },
];

export default function SiteEmployees() {
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSite, setFilterSite] = useState('All');
  const [filterRole, setFilterRole] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [showTabModal, setShowTabModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    site: '',
    user: '',
    role: 'site_supervisor',
    isActive: true,
  });
  const [tabFormData, setTabFormData] = useState({
    employeeId: '',
    tabName: '',
    tabPath: '',
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [employeeTabs, setEmployeeTabs] = useState([]);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    fetchEmployeeTabPermissions();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [employeesRes, sitesRes, usersRes] = await Promise.all([
        pb.collection('site_employees').getFullList({ 
          sort: '-created',
          expand: 'site,user,assignedBy' 
        }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('users').getFullList({ 
          filter: 'role ~ "site_" || role = "admin" || role = "accounts" || role = "store_keeper"' 
        }),
      ]);
      if (isMounted.current) {
        setEmployees(employeesRes);
        setSites(sitesRes);
        setUsers(usersRes);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load data');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const fetchEmployeeTabPermissions = async () => {
    try {
      const perms = await pb.collection('employee_tab_permissions').getFullList({
        expand: 'employee',
      });
      setEmployeeTabs(perms);
    } catch (err) {
      console.error('Error fetching employee tab permissions:', err);
      setEmployeeTabs([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleTabChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTabFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = {
        site: formData.site,
        user: formData.user,
        role: formData.role,
        isActive: formData.isActive,
        assignedBy: pb.authStore.model?.id,
        assignedAt: new Date().toISOString(),
      };

      await pb.collection('site_employees').create(data);
      toast.success('Employee added successfully');
      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error(err);
      let msg = err.message || 'Failed to add employee';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this employee from site?')) return;
    try {
      // Also delete tab permissions for this employee
      const perms = await pb.collection('employee_tab_permissions').getFullList({
        filter: `employee = "${id}"`,
      });
      for (const perm of perms) {
        await pb.collection('employee_tab_permissions').delete(perm.id);
      }
      await pb.collection('site_employees').delete(id);
      toast.success('Employee removed');
      fetchData();
      fetchEmployeeTabPermissions();
    } catch (err) {
      toast.error('Failed to remove: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ site: '', user: '', role: 'site_supervisor', isActive: true });
    setFormError('');
    setFormLoading(false);
  };

  // --- Tab Permission Handlers ----------------------------------
  const handleTabSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = {
        employee: tabFormData.employeeId,
        tabName: tabFormData.tabName,
        tabPath: tabFormData.tabPath,
        isActive: tabFormData.isActive,
        assignedBy: pb.authStore.model?.id,
        assignedAt: new Date().toISOString(),
      };

      await pb.collection('employee_tab_permissions').create(data);
      toast.success('Tab permission added');
      handleCloseTabModal();
      fetchEmployeeTabPermissions();
    } catch (err) {
      console.error(err);
      setFormError(err.message || 'Failed to add permission');
      setFormLoading(false);
    }
  };

  const handleDeleteTabPermission = async (id) => {
    if (!window.confirm('Remove this tab permission?')) return;
    try {
      await pb.collection('employee_tab_permissions').delete(id);
      toast.success('Tab permission removed');
      fetchEmployeeTabPermissions();
    } catch (err) {
      toast.error('Failed to remove: ' + err.message);
    }
  };

  const handleOpenTabModal = (employee) => {
    setSelectedEmployee(employee);
    setTabFormData({
      employeeId: employee.id,
      tabName: '',
      tabPath: '',
      isActive: true,
    });
    setShowTabModal(true);
  };

  const handleCloseTabModal = () => {
    setShowTabModal(false);
    setSelectedEmployee(null);
    setTabFormData({ employeeId: '', tabName: '', tabPath: '', isActive: true });
    setFormError('');
    setFormLoading(false);
  };

  const getSiteName = (id) => {
    const site = sites.find(s => s.id === id);
    return site?.name || 'Unknown Site';
  };

  const getUserName = (id) => {
    const user = users.find(u => u.id === id);
    return user?.name || user?.email || 'Unknown User';
  };

  const getEmployeeTabs = (employeeId) => {
    return employeeTabs.filter(t => t.employee === employeeId && t.isActive);
  };

  const isTabAssigned = (employeeId, tabPath) => {
    return employeeTabs.some(t => t.employee === employeeId && t.tabPath === tabPath && t.isActive);
  };

  const filteredEmployees = employees.filter(e => {
    const matchSite = filterSite === 'All' || e.site === filterSite;
    const matchRole = filterRole === 'All' || e.role === filterRole;
    const matchSearch = getSiteName(e.site).toLowerCase().includes(search.toLowerCase()) ||
                        getUserName(e.user).toLowerCase().includes(search.toLowerCase());
    return matchSite && matchRole && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* --- Header ---------------------------------------------- */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            Site Employees
          </h1>
          <p className="text-sm text-slate-500">Manage employees and their tab access</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => setShowModal(true)}>
          <UserPlus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      {/* --- Stats -------------------------------------------------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total Employees</p><p className="text-2xl font-bold text-slate-800">{employees.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Active</p><p className="text-2xl font-bold text-green-600">{employees.filter(e => e.isActive).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Inactive</p><p className="text-2xl font-bold text-red-600">{employees.filter(e => !e.isActive).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Tab Permissions</p><p className="text-2xl font-bold text-purple-600">{employeeTabs.length}</p></CardContent></Card>
      </div>

      {/* --- Filters ---------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by site or employee..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <select value={filterSite} onChange={(e) => setFilterSite(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option value="All">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option value="All">All Roles</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterSite('All'); setFilterRole('All'); }}>Clear</Button>
        <Button variant="outline" onClick={() => { fetchData(); fetchEmployeeTabPermissions(); }}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* --- Table --------------------------------------------------- */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Employee</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Role</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Tabs</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredEmployees.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No employees found.</td></tr>
              ) : (
                filteredEmployees.map(e => {
                  const tabs = getEmployeeTabs(e.id);
                  return (
                    <tr key={e.id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium">{getSiteName(e.site)}</td>
                      <td className="px-4 py-3">{getUserName(e.user)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                          {e.role?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {e.isActive ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {tabs.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {tabs.slice(0, 2).map(t => (
                              <Badge key={t.id} variant="outline" className="text-xs bg-blue-50">
                                {t.tabName}
                              </Badge>
                            ))}
                            {tabs.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{tabs.length - 2}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No tabs</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-purple-600 hover:bg-purple-50"
                            onClick={() => handleOpenTabModal(e)}
                            title="Manage Tabs"
                          >
                            <Shield size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(e.id)}
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* --- Add Employee Modal ------------------------------------ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">? Add Employee to Site</h2>
            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
                {formError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site *</label>
                <select name="site" value={formData.site} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white" required>
                  <option value="">Select Site</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Employee *</label>
                <select name="user" value={formData.user} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white" required>
                  <option value="">Select Employee</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                <select name="role" value={formData.role} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white" required>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="w-4 h-4 rounded border-slate-300" />
                <label className="text-sm text-slate-700">Active</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Adding...' : 'Add Employee'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Tab Permission Modal ---------------------------------- */}
      {showTabModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              ??? Tab Permissions for {getUserName(selectedEmployee.user)}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Site: {getSiteName(selectedEmployee.site)}
            </p>

            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
                {formError}
              </div>
            )}

            {/* --- Current Assigned Tabs --------------------------- */}
            {getEmployeeTabs(selectedEmployee.id).length > 0 && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-2">Assigned Tabs:</p>
                <div className="flex flex-wrap gap-2">
                  {getEmployeeTabs(selectedEmployee.id).map(t => (
                    <Badge key={t.id} variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1">
                      {t.tabName}
                      <button
                        onClick={() => handleDeleteTabPermission(t.id)}
                        className="ml-1 text-red-400 hover:text-red-600"
                      >
                        �
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* --- Add New Tab Form --------------------------------- */}
            <form onSubmit={handleTabSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tab Name *</label>
                <select 
                  name="tabName" 
                  value={tabFormData.tabName} 
                  onChange={(e) => {
                    const selected = AVAILABLE_TABS.find(t => t.label === e.target.value);
                    setTabFormData({
                      ...tabFormData,
                      tabName: selected?.label || '',
                      tabPath: selected?.path || '',
                    });
                  }} 
                  className="w-full px-3 py-2 border rounded-lg bg-white" 
                  required
                >
                  <option value="">Select Tab</option>
                  {AVAILABLE_TABS.map(t => {
                    const assigned = isTabAssigned(selectedEmployee.id, t.path);
                    return (
                      <option key={t.path} value={t.label} disabled={assigned}>
                        {t.label} {assigned ? '(Already Assigned)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Path</label>
                <Input value={tabFormData.tabPath} readOnly className="bg-slate-50" />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  name="isActive" 
                  checked={tabFormData.isActive} 
                  onChange={handleTabChange} 
                  className="w-4 h-4 rounded border-slate-300" 
                />
                <label className="text-sm text-slate-700">Active</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleCloseTabModal}>Close</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Adding...' : 'Add Permission'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}